// pages/organization/OrgExamsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import { httpRequest } from "../../utils/httpRequest";
import ExamCard from "../../components/organization/ExamCard";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Wand2, Search } from "lucide-react";
import { canWrite } from "../../components/auth/perm";
// (اختیاری) اگر داری: برای محدودسازی دسته‌ها به اسکوپ کاربر
import { computeAllowedCategorySet } from "../../utils/categories";
import ExamsTable from "../../components/organization/ExamsTable";
import { toast } from "react-toastify";

const OrgExamsPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  const user = JSON.parse(localStorage.getItem("user"));
  const mayCreate = canWrite(user, "exams");

  // --- state ---
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  // فیلترها
  const [categories, setCategories] = useState([]); // فقط level=3
  const [categoryId, setCategoryId] = useState(""); // مقدار انتخاب‌شده
  const [q, setQ] = useState(""); // متن سرچ
  const [typingQ, setTypingQ] = useState(""); // برای debounce

  // اسکوپ دسته‌بندی‌های کاربر (اگر در پروفایل ذخیره شده)
  const categoryScopes = Array.isArray(user?.categoryScopes)
    ? user.categoryScopes.map(String)
    : [];

  // دسته‌هایی که مجازند در «فیلتر» نمایش داده شوند
  const allowedCategoryIds = useMemo(() => {
    if (!categories.length) return new Set();
    if (!categoryScopes.length)
      return new Set(categories.map((c) => String(c._id)));
    // اگر util داری:
    try {
      const allowed = computeAllowedCategorySet(categories, categoryScopes);
      return allowed;
    } catch {
      // fallback: فقط دسته‌هایی که آیدیشان در اسکوپ آمده
      return new Set(categoryScopes);
    }
  }, [categories, categoryScopes]);

  // --- effects ---
  useEffect(() => {
    // لیست دسته‌ها برای فیلتر (level=3)
    (async () => {
      try {
        const query = new URLSearchParams({
          level: 3,
        });
        const res = await httpRequest(
          "GET",
          `/category?${query.toString()}`,
          {},
          {
            "x-access-token": user.token,
          }
        );
        const all = res?.data?.data || res?.data || [];
        setCategories(all);
      } catch {
        setCategories([]);
      }
    })();
  }, []); // mount

  // debounce سرچ
  useEffect(() => {
    const id = setTimeout(() => setQ(typingQ.trim()), 400);
    return () => clearTimeout(id);
  }, [typingQ]);

  // گرفتن آزمون‌ها با اعمال فیلترها
  useEffect(() => {
    fetchExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, categoryId]);

  const fetchExams = async () => {
    setLoading(true);
    try {
      // ساختن query string از params
      const query = new URLSearchParams();

      // فیلترها
      query.append("published", "true");
      if (q) query.append("q", q);
      if (categoryId) query.append("category", categoryId);

      // ساخت URL نهایی
      const url = `/exam?${query.toString()}`;

      const res = await httpRequest(
        "GET",
        url,
        {},
        {
          "x-access-token": user.token,
        }
      );

      const list = res?.data?.data || res?.data || [];
      setExams(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching exams", err);
      setExams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublish = async (examId) => {
    if (!examId) return;
    try {
      const res = await httpRequest(
        "PATCH",
        `/exam/${examId}/unpublish`,
        {}, // در این حالت فقط تغییر وضعیت
        { "x-access-token": user.token }
      );

      toast.success(t("exam_card.unpublish_success") || "آزمون لغو انتشار شد");
      // بعد از موفقیت، داده‌ها رو ریفرش کن
      fetchExams();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          t("exam_card.unpublish_error") ||
          "خطا در لغو انتشار آزمون"
      );
    }
  };

  // گزینه‌های دراپ‌داون دسته‌بندی با توجه به اسکوپ
  const categoryOptions = useMemo(() => {
    return categories
      .filter((c) => allowedCategoryIds.has(String(c._id)))
      .sort((a, b) => String(a.title).localeCompare(String(b.title)));
  }, [categories, allowedCategoryIds]);

  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("org_exams.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />

        <main className={mainSectionClass}>
          {/* Header + Actions */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800">
              {t("org_exams.page_header")}
            </h1>

            {mayCreate && (
              <Link
                to={"/organization/exam-builder"}
                className="bg-purple-600 rounded-lg px-4 py-2 text-white hover:bg-purple-700 duration-200 inline-flex items-center gap-2"
              >
                <Wand2 className="w-4 h-4" />
                {t("org_exams.go_to_builder")}
              </Link>
            )}
          </div>

          {/* Filters Row */}
          <div
            className={`mb-5 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end ${
              isRtl ? "text-right" : "text-left"
            }`}
          >
            {/* Search */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">
                {t("common.search")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={typingQ}
                  onChange={(e) => setTypingQ(e.target.value)}
                  placeholder={
                    t("org_exams.search_by_title") || "Search by title…"
                  }
                  className="w-full border rounded-lg px-3 py-2 pr-9"
                />
                <Search
                  className={`absolute top-1/2 -translate-y-1/2 ${
                    isRtl ? "left-2" : "right-2"
                  } text-gray-400`}
                  size={18}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("org_exams.filter_category")}
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">{t("org_exams.all_categories")}</option>
                {categoryOptions.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.title} {c.level ? ` (L${c.level})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <p className="text-sm text-gray-500">
              {t("org_exams.loading_message")}
            </p>
          ) : exams.length > 0 ? (
            <>
              {/* نمای جدولی زیر کارت‌ها */}
              <ExamsTable
                exams={exams}
                onManage={(ex) => console.log("manage", ex)}
                onView={(ex) => console.log("view", ex)}
                handleUnpublish={handleUnpublish}
                mayCreate={mayCreate}
              />
            </>
          ) : (
            <p className="text-sm text-gray-500">
              {t("org_exams.empty_state_message")}
            </p>
          )}
        </main>
      </div>
    </div>
  );
};

export default OrgExamsPage;
